import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Alert, ScrollView } from 'react-native';
import { updateTunnelUrlManually, getCurrentTunnelUrl, resetTunnelUrl } from '../services/ApiService';

interface DebugPanelProps {
  isVisible: boolean;
  onClose: () => void;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({ isVisible, onClose }) => {
  const [tunnelUrl, setTunnelUrl] = useState('');
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setCurrentUrl(getCurrentTunnelUrl());
    }
  }, [isVisible]);

  const handleUpdateTunnel = async () => {
    if (!tunnelUrl.trim()) {
      Alert.alert('Error', 'Please enter a tunnel URL');
      return;
    }

    setIsLoading(true);
    try {
      // Add https:// if not present
      let url = tunnelUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`;
      }

      await updateTunnelUrlManually(url);
      setCurrentUrl(url);
      setTunnelUrl('');
      Alert.alert('Success', 'Tunnel URL updated successfully!');
    } catch (error) {
      Alert.alert('Error', 'Failed to update tunnel URL');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    Alert.alert(
      'Reset Tunnel URL',
      'Are you sure you want to reset to automatic discovery?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset',
          style: 'destructive',
          onPress: () => {
            resetTunnelUrl();
            setCurrentUrl(null);
            Alert.alert('Success', 'Tunnel URL reset to automatic discovery');
          }
        }
      ]
    );
  };

  const handleQuickTunnel = () => {
    setTunnelUrl('https://toolbar-classic-native-patents.trycloudflare.com');
  };

  const handleNamedTunnel = () => {
    setTunnelUrl('https://api.rumdb.com');
  };

  if (!isVisible) return null;

  return (
    <View style={styles.overlay}>
      <View style={styles.panel}>
        <View style={styles.header}>
          <Text style={styles.title}>🔧 Debug Panel</Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Text style={styles.closeText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content}>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Current Tunnel URL:</Text>
            <Text style={styles.currentUrl}>
              {currentUrl || 'Automatic discovery'}
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Quick Actions:</Text>
            <TouchableOpacity onPress={handleQuickTunnel} style={styles.quickButton}>
              <Text style={styles.quickButtonText}>Quick Tunnel</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleNamedTunnel} style={styles.quickButton}>
              <Text style={styles.quickButtonText}>Named Tunnel (api.rumdb.com)</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleReset} style={[styles.quickButton, styles.resetButton]}>
              <Text style={styles.quickButtonText}>Reset to Auto</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Custom Tunnel URL:</Text>
            <TextInput
              style={styles.input}
              value={tunnelUrl}
              onChangeText={setTunnelUrl}
              placeholder="https://your-tunnel-url.trycloudflare.com"
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="url"
            />
            <TouchableOpacity
              onPress={handleUpdateTunnel}
              disabled={isLoading}
              style={[styles.updateButton, isLoading && styles.disabledButton]}
            >
              <Text style={styles.updateButtonText}>
                {isLoading ? 'Testing...' : 'Update Tunnel URL'}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Instructions:</Text>
            <Text style={styles.instruction}>
              1. Use "Quick Tunnel" for temporary testing{'\n'}
              2. Use "Named Tunnel" for your production API{'\n'}
              3. Or paste any custom tunnel URL above{'\n'}
              4. "Reset to Auto" returns to automatic discovery
            </Text>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  panel: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '90%',
    maxWidth: 400,
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  closeButton: {
    padding: 4,
  },
  closeText: {
    fontSize: 20,
    color: '#666',
  },
  content: {
    padding: 16,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
    color: '#333',
  },
  currentUrl: {
    fontSize: 14,
    color: '#666',
    fontFamily: 'monospace',
    backgroundColor: '#f5f5f5',
    padding: 8,
    borderRadius: 4,
  },
  quickButton: {
    backgroundColor: '#007AFF',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  quickButtonText: {
    color: 'white',
    textAlign: 'center',
    fontWeight: '500',
  },
  resetButton: {
    backgroundColor: '#FF3B30',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    marginBottom: 12,
  },
  updateButton: {
    backgroundColor: '#34C759',
    padding: 12,
    borderRadius: 8,
  },
  updateButtonText: {
    color: 'white',
    textAlign: 'center',
    fontWeight: '500',
  },
  disabledButton: {
    backgroundColor: '#ccc',
  },
  instruction: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
});
